// src/pages/EventDetailPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { BadgeView, printBadges, defaultDesign } from '../components/Badge'; // [marker: back-tags v1]
import postTicket, { SkippedDuplicate } from '../utils/postTicket'; // [marker: fixes-oct21 v1]
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import DataGrid from '../components/DataGrid'; // [marker: grid-tickets-invoices v1]
import EmailComposer from '../components/EmailComposer'; // [marker: email-compose v1]
import EventEmails from '../components/EventEmails'; // [marker: event-emails v1]
import { EventChecklist } from '../components/EventChecklist'; // [marker: event-checklist v1]
import CardPaymentsModal from '../components/CardPaymentsModal'; // [marker: pending-tickets v1]

export default function EventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isManagerOrAbove, isSuperAdmin, can } = useAuth();
  const canCheckIn = () => (can ? can(['events.checkin', 'events.edit']) : isManagerOrAbove()); // [marker: card-details v1]
  const [event, setEvent] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false); /* [marker: grid-tickets-invoices v1] */
  const [composeTicketId, setComposeTicketId] = useState(null); /* [marker: email-compose v1] */
  const [sponsorships, setSponsorships] = useState([]);
  const [activeTab, setActiveTab] = useState('info');
  const [loading, setLoading] = useState(true);
  const [ticketSearch, setTicketSearch] = useState('');
  const [showEditEvent, setShowEditEvent] = useState(false);

  const [ticketTypeModal, setTicketTypeModal] = useState({ open: false, ticketType: null });
  const [productOptions, setProductOptions] = useState([]);
  const productsLoaded = useRef(false);

  const [addTicketOpen, setAddTicketOpen] = useState(false);
  const [nametagsOpen, setNametagsOpen] = useState(false);
  const [checkInTag, setCheckInTag] = useState(null);
  const [addSponsorshipOpen, setAddSponsorshipOpen] = useState(false);
  const [editingSponsorship, setEditingSponsorship] = useState(null);
  const [viewingSponsor, setViewingSponsor] = useState(null);
  const [tierModal, setTierModal] = useState({ open: false, tier: null });
  const [editingTicket, setEditingTicket] = useState(null);
  const [guestNamesOpen, setGuestNamesOpen] = useState(false); /* [marker: guest-names v1] */
  const [refundTicket, setRefundTicket] = useState(null);
  const [attendeeView, setAttendeeView] = useState('registered'); // [marker: pending-tickets v1] registered | pending
  const [cardPaymentsOpen, setCardPaymentsOpen] = useState(false);

  const flyerInputRef = useRef(null);
  const [uploadingFlyer, setUploadingFlyer] = useState(false);

  const loadEvent = () => {
    api.get(`/events/${id}`)
      .then(res => setEvent(res.data))
      .catch(() => toast.error('Failed to load event.'))
      .finally(() => setLoading(false));
  };

  // [marker: tickets-load-all v1]
  // The tickets endpoint is paginated (default 100/page). Fetching a single
  // page silently dropped the earliest tickets on large events. Fetch page 1,
  // then follow the reported page count to load EVERY ticket.
  const loadTickets = async () => {
    const PAGE_SIZE = 200;
    const buildQs = (page) => {
      const qs = new URLSearchParams({ limit: String(PAGE_SIZE), page: String(page) });
      if (ticketSearch) qs.set('search', ticketSearch);
      return qs.toString();
    };
    setTicketsLoading(true);
    try {
      const first = await api.get(`/events/${id}/tickets?${buildQs(1)}`);
      let all = first.data.data || [];
      const totalPages = first.data.pagination?.totalPages
        || Math.ceil((first.data.pagination?.total || all.length) / PAGE_SIZE)
        || 1;
      if (totalPages > 1) {
        const rest = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            api.get(`/events/${id}/tickets?${buildQs(i + 2)}`)
              .then(r => r.data.data || [])
              .catch(() => [])
          )
        );
        rest.forEach(chunk => { all = all.concat(chunk); });
      }
      setTickets(all);
    } catch (err) {
      console.error(err);
    } finally {
      setTicketsLoading(false);
    }
  };

  const loadSponsorships = () => {
    api.get(`/events/${id}/sponsorships`)
      .then(res => setSponsorships(res.data.data || res.data || []))
      .catch(err => { console.error('Failed to load sponsorships', err); setSponsorships([]); });
  };

  const handleExportAttendees = async () => {
    try {
      const res = await api.get(`/reports/export/event-attendees/${id}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `njjba_event_${id}_attendees.csv`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to export attendees.');
    }
  };

  const handleDeleteEvent = async () => {
    if (!window.confirm(
      `Delete event "${event.name}"?\n\nThis cannot be undone. ` +
      `Events with ticket sales cannot be deleted — they must be cancelled instead.`
    )) return;
    try {
      await api.delete(`/events/${id}`);
      toast.success('Event deleted.');
      navigate('/events');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete event.');
    }
  };

  useEffect(() => { loadEvent(); }, [id]);
  useEffect(() => { if (activeTab === 'tickets') loadTickets(); }, [activeTab, ticketSearch]);
  useEffect(() => { if (activeTab === 'sponsorships') loadSponsorships(); }, [activeTab, id]);

  const ensureProductsLoaded = async () => {
    if (productsLoaded.current) return;
    try {
      const res = await api.get('/invoice-items?is_active=true');
      setProductOptions(res.data);
      productsLoaded.current = true;
    } catch (err) {
      console.error('Failed to load products for ticket-type picker', err);
    }
  };

  const handleFlyerSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Flyer must be under 10 MB.'); return; }
    setUploadingFlyer(true);
    try {
      const fd = new FormData(); fd.append('flyer', file);
      await api.post(`/events/${id}/flyer`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Flyer uploaded.'); loadEvent();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Flyer upload failed.');
    } finally {
      setUploadingFlyer(false);
      if (flyerInputRef.current) flyerInputRef.current.value = '';
    }
  };

  const handleRemoveFlyer = async () => {
    if (!window.confirm('Remove the current flyer?')) return;
    try {
      await api.delete(`/events/${id}/flyer`);
      toast.success('Flyer removed.'); loadEvent();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove flyer.');
    }
  };

  const openAddTicketType = async () => { await ensureProductsLoaded(); setTicketTypeModal({ open: true, ticketType: null }); };
  const openEditTicketType = async (ticketType) => { await ensureProductsLoaded(); setTicketTypeModal({ open: true, ticketType }); };
  const closeTicketTypeModal = () => setTicketTypeModal({ open: false, ticketType: null });
  // [marker: confirm-polish v1] after the first ticket type, ask which confirmation email this event should use
  const [confirmPrompt, setConfirmPrompt] = useState(false);
  const [openEmailKind, setOpenEmailKind] = useState(null);
  const handleTicketTypeSaved = () => {
    const wasNew = !ticketTypeModal.ticketType;
    closeTicketTypeModal(); loadEvent();
    if (wasNew && isManagerOrAbove()) {
      api.get(`/event-checklist/${id}`).then(r => {
        const it = (r.data.items || []).find(i => i.key === 'confirmation');
        if (it && it.state === 'todo') setConfirmPrompt(true);
      }).catch(() => {});
    }
  };
  const chooseStandardConfirmation = async () => {
    setConfirmPrompt(false);
    try { await api.post(`/event-checklist/${id}/confirmation`, { status: 'done' }); toast.success('This event will use the standard confirmation email.'); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not save that choice.'); }
  };

  const handleDeleteTicketType = async (tt) => {
    if (!window.confirm(`Delete ticket type "${tt.name}"?`)) return;
    try {
      await api.delete(`/ticket-types/${tt.id}`);
      toast.success('Ticket type deleted.'); loadEvent();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete ticket type.');
    }
  };

  // ── Sponsorship-tier handlers (mirror ticket types) ──────────────────────────
  const openAddTier = () => setTierModal({ open: true, tier: null });
  const openEditTier = (tier) => setTierModal({ open: true, tier });
  const closeTierModal = () => setTierModal({ open: false, tier: null });
  const handleTierSaved = () => { closeTierModal(); loadEvent(); };

  const handleDeleteTier = async (t) => {
    if (!window.confirm(`Delete sponsorship tier "${t.name}"?\n\nExisting sponsorships keep their amount but lose the tier label.`)) return;
    try {
      await api.delete(`/sponsorship-tiers/${t.id}`);
      toast.success('Sponsorship tier deleted.'); loadEvent();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete tier.');
    }
  };

  const handleCheckIn = async (ticket) => {
    try {
      await api.post(`/tickets/${ticket.id}/check-in`);
      toast.success('Checked in!'); loadTickets();
      setCheckInTag({ ticket_id: ticket.id, first_name: ticket.buyer_first_name || '', last_name: ticket.buyer_last_name || '',
        company: ticket.buyer_business_name || '', ticket_type: ticket.ticket_type_name || '' }); // [marker: back-tags v1]
    } catch (err) {
      toast.error(err.response?.data?.error || 'Check-in failed.');
    }
  };

  const handleUndoCheckIn = async (ticketId) => {
    if (!window.confirm('Undo this check-in? The attendee will be marked as not checked in.')) return;
    try {
      await api.patch(`/tickets/${ticketId}/undo-check-in`);
      toast.success('Check-in undone.'); loadTickets();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not undo check-in.');
    }
  };


  const handleDeleteTicket = async (ticket) => {
    const willRefund = !!ticket.cardknox_transaction_id && ticket.payment_status !== 'refunded';
    const who = `${ticket.buyer_first_name || ''} ${ticket.buyer_last_name || ''}`.trim();
    const msg = willRefund
      ? `Refund and delete ticket ${ticket.ticket_number}${who ? ' for ' + who : ''}? This issues a Cardknox refund of ${parseFloat(ticket.amount_paid || 0).toFixed(2)} and then removes the ticket. This cannot be undone.`
      : `Delete ticket ${ticket.ticket_number}${who ? ' for ' + who : ''}? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      if (willRefund) {
        await api.post('/payments/refund-ticket', { ticketId: ticket.id, reason: 'Ticket deleted' });
      }
      await api.delete(`/tickets/${ticket.id}`);
      toast.success(willRefund ? 'Refunded and deleted.' : 'Ticket deleted.');
      loadTickets(); loadEvent();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete ticket.');
    }
  };

  const handleDeleteSponsorship = async (s) => {
    if (!window.confirm(`Delete sponsorship from ${s.sponsor_name || 'this sponsor'}?\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/sponsorships/${s.id}`);
      toast.success('Sponsorship deleted.'); loadSponsorships(); loadEvent();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete sponsorship.');
    }
  };

  const handleMarkSponsorshipPaid = async (s) => {
    if (!window.confirm(`Mark ${s.sponsor_name} as paid?`)) return;
    try {
      await api.patch(`/sponsorships/${s.id}`, {
        paymentStatus: 'paid',
        paidAt: new Date().toISOString(),
      });
      toast.success('Marked as paid.'); loadSponsorships(); loadEvent();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update sponsorship.');
    }
  };

  const handleGenerateNametags = async () => {
    try {
      const res = await api.post(`/nametags/events/${id}/generate`, { layout: 'avery_5395' });
      const pdf = await api.get(`/nametags/download/${res.data.filename}`, { responseType: 'blob' });
      const blob = new Blob([pdf.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', res.data.filename);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Generated ${res.data.count} name tags.`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to generate name tags.');
    }
  };

  if (loading) return <div className="loading-page"><div className="spinner" style={{ width: 32, height: 32 }} /></div>;
  if (!event) return <div className="empty-state"><h3>Event not found</h3><Link to="/events">← Back</Link></div>;

  const revenue = event.revenue || {};

  return (
    <div>
      <div className="page-header">
        <div>
          <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginBottom: 4 }}>
            <Link to="/events" style={{ color: 'var(--gray-400)' }}>Events</Link> / {event.name}
          </div>
          <h1 className="page-title">{event.name}</h1>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
            <span className={`badge badge-${event.status}`}>{event.status}</span>
            <span className="text-muted">{fmtDate(event.event_date)}</span>
            {event.venue && <span className="text-muted">· {event.venue}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {isManagerOrAbove() && <button className="btn btn-secondary" onClick={() => setShowEditEvent(true)}>Edit</button>}
          <button className="btn btn-secondary" onClick={() => setNametagsOpen(true)}
                  disabled={!revenue.tickets_sold || revenue.tickets_sold === 0}
                  title={!revenue.tickets_sold ? 'No attendees yet — sell or import tickets first' : ''}>
            🏷 Name Tags
          </button>
          <a href={`/checkin/checkin.html?event=${id}`} target="_blank" rel="noreferrer" className="btn btn-secondary" title="Scan QR codes from tickets">📱 QR Check-In</a>
          <a href={`/kiosk/${id}`} target="_blank" rel="noreferrer" className="btn btn-primary" title="Open on an iPad at the door — guests find their name, check in and print their tag">🖥 Self Check-In Kiosk</a>
          {isManagerOrAbove() && (!event.tickets_sold || event.tickets_sold === 0) && (
            <button className="btn btn-danger" onClick={handleDeleteEvent}
                    title="Delete this event (only allowed when there are no ticket sales)">Delete</button>
          )}
        </div>
      </div>

      <div className="stat-grid" style={{ marginBottom: 24 }}>
        <div className="stat-card">
          <div className="stat-label">Tickets Sold</div>
          <div className="stat-value">{revenue.tickets_sold || 0}</div>
          {event.total_capacity && <div className="stat-sub">of {event.total_capacity} capacity</div>}
        </div>
        <div className="stat-card">
          <div className="stat-label">Checked In</div>
          <div className="stat-value">{revenue.checked_in_count || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Ticket Revenue</div>
          <div className="stat-value">${parseFloat(revenue.ticket_revenue || 0).toLocaleString()}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Sponsors</div>
          <div className="stat-value">{revenue.total_sponsors || 0}</div>
          <div className="stat-sub">${parseFloat(revenue.paid_revenue || 0).toLocaleString()} paid</div>
        </div>
      </div>

      <div className="tabs">
        {['info','tickets','sponsorships','invoices','emails'].map(tab => ( /* [marker: invoice-links v1] [marker: event-emails v1] */
          <button key={tab} className={`tab-btn${activeTab === tab ? ' active' : ''}`} onClick={() => setActiveTab(tab)}>
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {activeTab === 'info' && (
        <>
          {/* [marker: event-checklist v1] */}
          <EventChecklist eventId={id} refreshKey={`${event.updated_at}-${(event.ticketTypes || []).length}-${(event.sponsorshipTiers || []).length}-${(event.promoCodes || []).length}-${event.attendee_tag_id}`} onGo={(key) => {
            if (key === 'promo_codes') navigate('/promo-codes');
            else if (key === 'catalog_items') navigate('/products');
            else if (key === 'name_tag_design') navigate(`/events/${id}/badges`);
            else if (key === 'attendee_tag') setShowEditEvent(true);
            else if (['confirmation', 'day_before', 'post_event', 'sponsor_email'].includes(key)) setActiveTab('emails');
            else if (key === 'links_tested') window.open(`${window.location.origin}/embed/events/${id}`, '_blank', 'noopener');
            else { const el = document.getElementById(key === 'sponsorships' ? 'card-sponsorship-tiers' : 'card-ticket-types'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }
          }} />
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header">
              <span className="card-title">Event Flyer</span>
              {isManagerOrAbove() && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <input ref={flyerInputRef} type="file" accept="image/jpeg,image/png,application/pdf"
                         onChange={handleFlyerSelected} style={{ display: 'none' }} />
                  <button className="btn btn-secondary btn-sm"
                          onClick={() => flyerInputRef.current?.click()} disabled={uploadingFlyer}>
                    {uploadingFlyer ? 'Uploading…' : (event.flyer_filename ? 'Replace Flyer' : 'Upload Flyer')}
                  </button>
                  {event.flyer_filename && (
                    <button className="btn btn-ghost btn-sm" onClick={handleRemoveFlyer}>Remove</button>
                  )}
                </div>
              )}
            </div>
            <div className="card-body">
              {event.flyer_filename ? (
                <FlyerPreview eventId={id} filename={event.flyer_filename} originalName={event.flyer_original_name} />
              ) : (
                <p className="text-muted">No flyer uploaded. JPG, PNG, or PDF — max 10 MB.</p>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div className="card">
              <div className="card-header"><span className="card-title">Event Details</span></div>
              <div className="card-body">
                <InfoRow label="Date" value={fmtDate(event.event_date)} />
                <InfoRow label="Time" value={event.start_time ? `${event.start_time}${event.end_time ? ` – ${event.end_time}` : ''}` : null} />
                <InfoRow label="Venue" value={event.venue} />
                <InfoRow label="Address" value={event.location_address} />
                <InfoRow label="Capacity" value={event.total_capacity?.toString()} />
                <InfoRow label="Visibility" value={event.visibility} />
                <InfoRow label="Registration" value={event.registration_open_date ? `${fmtDate(event.registration_open_date)} – ${fmtDate(event.registration_close_date)}` : null} />
              </div>
            </div>
            <div id="card-ticket-types" className="card">
              <div className="card-header">
                <span className="card-title">Ticket Types</span>
                {isManagerOrAbove() && <button className="btn btn-primary btn-sm" onClick={openAddTicketType}>+ Add</button>}
              </div>
              <div className="card-body">
                {event.ticketTypes?.length === 0 ? (
                  <p className="text-muted">No ticket types defined</p>
                ) : event.ticketTypes?.map(tt => (
                  <div key={tt.id} style={{
                    padding: '10px 0', borderBottom: '1px solid var(--gray-100)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    gap: 10, opacity: tt.is_active === false ? 0.5 : 1,
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {tt.name}
                        {tt.catalog_item_name && (
                          <span className="badge badge-info" style={{ fontSize: '0.65rem' }}>Product: {tt.catalog_item_name}</span>
                        )}
                        {tt.is_active === false && <span className="badge badge-muted" style={{ fontSize: '0.65rem' }}>Inactive</span>}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>
                        Member: ${tt.member_price} · Non-member: ${tt.non_member_price}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)' }}>
                        {tt.quantity_sold || 0} sold{tt.quantity_available ? ` of ${tt.quantity_available}` : ' (unlimited)'}
                      </div>
                    </div>
                    {isManagerOrAbove() && (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => openEditTicketType(tt)} title="Edit">✎</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => handleDeleteTicketType(tt)} title="Delete">🗑</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sponsorship Tiers — mirrors Ticket Types, single price (no member/non-member) */}
          <div id="card-sponsorship-tiers" className="card" style={{ marginTop: 20 }}>
            <div className="card-header">
              <span className="card-title">Sponsorship Tiers</span>
              {isManagerOrAbove() && <button className="btn btn-primary btn-sm" onClick={openAddTier}>+ Add</button>}
            </div>
            <div className="card-body">
              {(!event.sponsorshipTiers || event.sponsorshipTiers.length === 0) ? (
                <p className="text-muted">No sponsorship tiers yet. Add one so it shows up in the "Add Sponsorship" dropdown.</p>
              ) : event.sponsorshipTiers.map(tier => (
                <div key={tier.id} style={{
                  padding: '10px 0', borderBottom: '1px solid var(--gray-100)',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{tier.name}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>
                      ${parseFloat(tier.price ?? 0).toFixed(2)}
                      {tier.included_tickets ? ` · ${tier.included_tickets} ticket${tier.included_tickets === 1 ? '' : 's'} included` : ''}
                    </div>
                    {tier.benefits_description && (
                      <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)' }}>{tier.benefits_description}</div>
                    )}
                  </div>
                  {isManagerOrAbove() && (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => openEditTier(tier)} title="Edit">✎</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => handleDeleteTier(tier)} title="Delete">🗑</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {activeTab === 'tickets' && (() => {
        // [marker: pending-tickets v1] people who started checkout but whose payment hasn't come in are kept apart
        const pendingTickets = tickets.filter(t => t.payment_status === 'pending');
        const registeredTickets = tickets.filter(t => t.payment_status !== 'pending');
        const pendingOrders = Object.values(pendingTickets.reduce((m, t) => {
          const k = t.order_ref || t.ticket_number;
          const o = m[k] || (m[k] = { ref: k, who: '', amount: 0, created: t.created_at });
          o.amount += parseFloat(t.amount_paid) || 0;
          const nm = [t.buyer_first_name, t.buyer_last_name].filter(Boolean).join(' ');
          if (nm && !o.who.includes(nm)) o.who = o.who ? `${o.who} / ${nm}` : nm;
          return m;
        }, {}));
        const shownTickets = attendeeView === 'pending' ? pendingTickets : registeredTickets;
        return (
        <div className="card">
          <div className="card-header">
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <span className="card-title" style={{ marginRight: 6 }}>Attendees</span>
              <button type="button" className={`btn btn-sm ${attendeeView === 'registered' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setAttendeeView('registered')}>
                Registered ({registeredTickets.length})
              </button>
              <button type="button" className={`btn btn-sm ${attendeeView === 'pending' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setAttendeeView('pending')}
                style={attendeeView !== 'pending' && pendingTickets.length ? { background: '#fff4d6', borderColor: '#e0b44c', color: '#7a5200' } : undefined}>
                Pending payment ({pendingTickets.length})
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {attendeeView === 'pending' && isManagerOrAbove() && (
                <button type="button" onClick={() => setCardPaymentsOpen(true)} className="btn btn-secondary btn-sm"
                  title="Card payments Cardknox took that the app couldn't match to an order">💳 Match card payments</button>
              )}
              {/* [marker: guest-names v1] tickets still carrying the payer's name or "Guest of …" */}
              {(() => { const n = tickets.filter(t => t.needs_name).length; return (
                <button type="button" onClick={() => setGuestNamesOpen(true)} className="btn btn-secondary btn-sm"
                  style={n ? { background: '#fff4d6', borderColor: '#e0b44c', color: '#7a5200' } : undefined}
                  title="Enter the names of the guests on each ticket">
                  {n ? `Guest names (${n} needed)` : 'Guest names'}
                </button>); })()}
              <button type="button" onClick={handleExportAttendees} className="btn btn-secondary btn-sm">Export CSV</button>
              {isManagerOrAbove() && event.ticketTypes?.length > 0 && (
                <button type="button" onClick={() => setAddTicketOpen(true)} className="btn btn-primary btn-sm">+ Add Ticket</button>
              )}
            </div>
          </div>
          {/* [marker: grid-tickets-invoices v1] Excel-style attendee grid */}
          <div style={{ padding: 8 }}>
            <DataGrid
              gridKey="event-tickets"
              columns={makeTicketColumns({ isManagerOrAbove, isSuperAdmin, canCheckIn, handleCheckIn, handleUndoCheckIn, setEditingTicket, setRefundTicket, handleDeleteTicket, setComposeTicketId })}
              rows={shownTickets}
              loading={ticketsLoading}
              height="calc(100vh - 360px)"
              emptyText={attendeeView === 'pending' ? 'Nobody is waiting on a payment.' : 'No attendees yet.'}
              totals={['amount_paid']}
              persistFilters={false}
            />
          </div>
          {attendeeView === 'pending' && (
            <div style={{ fontSize: '0.78rem', color: 'var(--gray-600)', padding: '0 12px 10px' }}>
              These people filled in the form but the app has no card payment for them yet. If one of them did pay, press
              <strong> 💳 Match card payments</strong> and connect their payment; if they paid another way, open the ticket (✎) and set it to Paid.
            </div>
          )}
          {cardPaymentsOpen && (
            <CardPaymentsModal pendingOrders={pendingOrders} onClose={() => setCardPaymentsOpen(false)} onDone={loadTickets} />
          )}
        </div>
        );
      })()}

      {/* Invoices for this event — [marker: invoice-links v1] */}
      {activeTab === 'invoices' && <EventInvoices eventId={id} />}
      {activeTab === 'emails' && <EventEmails eventId={id} event={event} canEdit={isManagerOrAbove()} openKind={openEmailKind} onOpened={() => setOpenEmailKind(null)} />} {/* [marker: event-emails v1] [marker: confirm-polish v1] */}
      {confirmPrompt && ( /* [marker: confirm-polish v1] */
        <div className="modal-overlay" onClick={() => setConfirmPrompt(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header"><span className="modal-title">Which confirmation email should ticket buyers get?</span>
              <button className="btn btn-ghost btn-icon" onClick={() => setConfirmPrompt(false)}>✕</button></div>
            <div className="modal-body" style={{ display: 'grid', gap: 10 }}>
              <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--gray-600)' }}>Everyone who gets a ticket for {event.name} receives a confirmation email. Choose which one.</p>
              <button type="button" className="btn btn-secondary" style={{ textAlign: 'left', padding: 14, height: 'auto', display: 'block' }} onClick={chooseStandardConfirmation}>
                <strong>The standard confirmation</strong>
                <div style={{ fontSize: '0.8rem', fontWeight: 400, color: 'var(--gray-500)', marginTop: 4 }}>Date and time, venue with directions, name on the ticket, ticket number, check-in link and add-to-calendar link.</div>
              </button>
              <button type="button" className="btn btn-secondary" style={{ textAlign: 'left', padding: 14, height: 'auto', display: 'block' }}
                onClick={() => { setConfirmPrompt(false); setOpenEmailKind('confirmation'); setActiveTab('emails'); }}>
                <strong>A custom confirmation for this event</strong>
                <div style={{ fontSize: '0.8rem', fontWeight: 400, color: 'var(--gray-500)', marginTop: 4 }}>Starts from the standard text; add parking, dress code, agenda or anything else for this event.</div>
              </button>
            </div>
            <div className="modal-footer"><button className="btn btn-ghost" onClick={() => setConfirmPrompt(false)}>Decide later</button></div>
          </div>
        </div>
      )}

      {activeTab === 'sponsorships' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Sponsors</span>
            {isManagerOrAbove() && (
              <button
                type="button"
                onClick={() => { setEditingSponsorship(null); setAddSponsorshipOpen(true); }}
                className="btn btn-primary btn-sm">
                + Add Sponsorship
              </button>
            )}
          </div>
          <div className="table-wrap" style={{ border: 'none' }}>
            <table>
              <thead><tr><th>Sponsor</th><th>Sponsorship</th><th>Amount</th><th>Status</th><th>Invoice</th><th></th></tr></thead>
              <tbody>
                {sponsorships.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <div className="empty-state" style={{ padding: 24 }}>
                        <p>No sponsors yet</p>
                        {isManagerOrAbove() && (
                          <p style={{ fontSize: '0.85rem', color: 'var(--gray-500)', marginTop: 8 }}>
                            Click "+ Add Sponsorship" to record one.
                          </p>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : sponsorships.map(s => (
                  <tr key={s.id}>
                    <td>
                      <button
                        onClick={() => setViewingSponsor(s)}
                        style={{ background: 'none', border: 'none', padding: 0, font: 'inherit',
                                 color: 'var(--primary, #2563eb)', cursor: 'pointer', textAlign: 'left' }}
                        className="td-primary" title="View sponsor details">
                        {s.sponsor_name || s.business_name || '—'}
                      </button>
                      {s.sponsor_email && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>{s.sponsor_email}</div>
                      )}
                    </td>
                    <td style={{ fontSize: '0.85rem' }}>
                      {s.tier_name || <span style={{ color: 'var(--gray-400)' }}>No tier set</span>}
                      {s.item_id && <div style={{ fontSize: '0.72rem' }}><Link to="/products">Item List →</Link></div>}
                    </td>
                    <td style={{ fontSize: '0.875rem' }}>${parseFloat(s.amount || 0).toFixed(2)}</td>
                    <td><span className={`badge badge-${s.payment_status}`}>{s.payment_status?.replace('_', ' ')}</span></td>
                    {/* [marker: item-list v1] linked invoice */}
                    <td style={{ fontSize: '0.8rem' }}>
                      {s.invoice_id ? (
                        <Link to={`/invoices/${s.invoice_id}`}>
                          {s.invoice_number}
                          <div><span className={`badge badge-${s.invoice_status}`} style={{ fontSize: '0.62rem' }}>{s.invoice_status?.replace('_', ' ')}</span></div>
                        </Link>
                      ) : <span style={{ color: 'var(--gray-400)' }}>—</span>}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {isManagerOrAbove() && (
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          {s.payment_status !== 'paid' && (
                            <button className="btn btn-secondary btn-sm"
                                    onClick={() => handleMarkSponsorshipPaid(s)} title="Mark as paid">
                              Mark Paid
                            </button>
                          )}
                          <button className="btn btn-ghost btn-sm"
                                  onClick={() => { setEditingSponsorship(s); setAddSponsorshipOpen(true); }}
                                  title="Edit">✎</button>
                          <button className="btn btn-ghost btn-sm"
                                  onClick={() => handleDeleteSponsorship(s)} title="Delete">🗑</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showEditEvent && (
        <EditEventModal event={event} onClose={() => setShowEditEvent(false)}
                        onSaved={() => { setShowEditEvent(false); loadEvent(); }} />
      )}

      {ticketTypeModal.open && (
        <TicketTypeModal eventId={id} ticketType={ticketTypeModal.ticketType}
                         productOptions={productOptions}
                         onClose={closeTicketTypeModal} onSaved={handleTicketTypeSaved} />
      )}

      {tierModal.open && (
        <SponsorshipTierModal eventId={id} tier={tierModal.tier}
                              onClose={closeTierModal} onSaved={handleTierSaved} />
      )}

      {addTicketOpen && (
        <AddTicketModal eventId={id} event={event} onClose={() => setAddTicketOpen(false)}
                        onSaved={(emailTicketId) => { setAddTicketOpen(false); loadTickets(); loadEvent(); if (emailTicketId) setComposeTicketId(emailTicketId); }} />
      )}
      {composeTicketId && (
        <EmailComposer
          kind="ticket_confirmation" id={composeTicketId}
          title="Email Ticket Confirmation" sendLabel="Send Confirmation"
          onClose={() => setComposeTicketId(null)}
          onSent={() => setComposeTicketId(null)}
        />
      )}

      {nametagsOpen && (
        <NameTagsModal eventId={id} onClose={() => setNametagsOpen(false)} />
      )}
      {checkInTag && (
        <CheckInTagModal tag={checkInTag} eventId={id} onClose={() => setCheckInTag(null)} />
      )}

      {guestNamesOpen && ( /* [marker: guest-names v1] */
        <GuestNamesModal eventId={id} canEdit={isManagerOrAbove()} onClose={() => setGuestNamesOpen(false)}
          onSaved={() => { loadTickets(); }} />
      )}
      {editingTicket && (
        <EditTicketModal ticket={editingTicket} onClose={() => setEditingTicket(null)}
          onSaved={() => { setEditingTicket(null); loadTickets(); loadEvent(); }} />
      )}
      {refundTicket && (
        <RefundTicketModal ticket={refundTicket} onClose={() => setRefundTicket(null)}
          onSaved={() => { setRefundTicket(null); loadTickets(); loadEvent(); }} />
      )}

      {addSponsorshipOpen && (
        <AddSponsorshipModal
          eventId={id}
          event={event}
          sponsorship={editingSponsorship}
          onClose={() => { setAddSponsorshipOpen(false); setEditingSponsorship(null); }}
          onSaved={() => {
            setAddSponsorshipOpen(false); setEditingSponsorship(null);
            loadSponsorships(); loadEvent();
          }}
        />
      )}
      {viewingSponsor && (
        <SponsorDetailModal sponsor={viewingSponsor} onClose={() => setViewingSponsor(null)} />
      )}
    </div>
  );
}

function EditTicketModal({ ticket, onClose, onSaved }) {
  const [form, setForm] = useState({
    buyerFirstName: ticket.buyer_first_name || '',
    buyerLastName: ticket.buyer_last_name || '',
    buyerEmail: ticket.buyer_email || '',
    buyerBusinessName: ticket.buyer_business_name || '',
    paymentStatus: ticket.payment_status || 'paid',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/tickets/${ticket.id}`, form);
      toast.success('Ticket updated.'); onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update ticket.');
    } finally { setSaving(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div className="modal-header"><span className="modal-title">Edit Attendee — {ticket.ticket_number}</span><button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button></div>
        <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
          <div className="form-row">
            <div className="form-group"><label className="form-label">First Name</label><input className="form-input" value={form.buyerFirstName} onChange={e => set('buyerFirstName', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Last Name</label><input className="form-input" value={form.buyerLastName} onChange={e => set('buyerLastName', e.target.value)} /></div>
          </div>
          <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" value={form.buyerEmail} onChange={e => set('buyerEmail', e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Organization</label><input className="form-input" value={form.buyerBusinessName} onChange={e => set('buyerBusinessName', e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Payment Status</label>
            <select className="form-select" value={form.paymentStatus} onChange={e => set('paymentStatus', e.target.value)}>
              <option value="paid">paid</option>
              <option value="unpaid">unpaid</option>
              <option value="comp">comp</option>
              <option value="refunded">refunded</option>
            </select>
          </div>
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button></div>
      </div>
    </div>
  );
}

function RefundTicketModal({ ticket, onClose, onSaved }) {
  const [amount, setAmount] = useState(ticket.amount_paid != null ? String(ticket.amount_paid) : '');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const body = { ticketId: ticket.id, reason };
      const amt = parseFloat(amount);
      if (!isNaN(amt) && amt > 0) body.amount = amt;
      const res = await api.post('/payments/refund-ticket', body);
      toast.success(`Refunded ${res.data.amountRefunded != null ? res.data.amountRefunded : amount}.`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Refund failed.');
    } finally { setSaving(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <div className="modal-header"><span className="modal-title">Refund Ticket — {ticket.ticket_number}</span><button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button></div>
        <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
          <p style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>Issues a real Cardknox refund for this card ticket purchase.</p>
          <div className="form-group"><label className="form-label">Amount</label>
            <input className="form-input" type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} />
            <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: 4 }}>Leave as-is for a full refund.</div>
          </div>
          <div className="form-group"><label className="form-label">Reason (optional)</label><input className="form-input" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. attendee cancelled" /></div>
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button><button className="btn btn-danger" onClick={submit} disabled={saving}>{saving ? 'Refunding…' : 'Issue Refund'}</button></div>
      </div>
    </div>
  );
}

// [marker: back-tags v1] name tags come from the design (Name Tags → Design) and print on the device's printer
function useBadgeDesign(eventId) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/badges/design?eventId=${eventId}`).then(r => setD({ design: r.data.design || defaultDesign(r.data.orgLogoUrl), event: r.data.event }))
      .catch(() => setD({ design: defaultDesign(null), event: null }));
  }, [eventId]);
  return d;
}

function CheckInTagModal({ tag, eventId, onClose }) {
  const b = useBadgeDesign(eventId);
  const print = async () => {
    if (!b) return;
    await printBadges(b.design, [tag], b.event);
    api.post('/badges/printed', { ticketIds: [tag.ticket_id] }).catch(() => {});
  };
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2>Checked In ✓</h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ textAlign: 'center' }}>
          {b ? <div style={{ display: 'inline-block' }}><BadgeView design={b.design} person={tag} event={b.event} scale={1} /></div> : <div className="spinner" />}
          <p style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginTop: 12 }}>Print a name tag for this attendee, or close to keep checking people in.</p>
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
          <button className="btn btn-primary" onClick={print} disabled={!b}>🖨 Print Name Tag</button>
        </div>
      </div>
    </div>
  );
}

function NameTagsModal({ eventId, onClose }) {
  const b = useBadgeDesign(eventId);
  const [who, setWho] = useState('all');
  const [people, setPeople] = useState(null);
  const [printing, setPrinting] = useState(false);
  useEffect(() => {
    setPeople(null);
    api.get(`/badges/events/${eventId}/people?who=${who}`).then(r => setPeople(r.data || [])).catch(() => setPeople([]));
  }, [eventId, who]);
  const print = async () => {
    if (!people || !people.length || !b) return;
    setPrinting(true);
    try {
      await printBadges(b.design, people, b.event);
      if (window.confirm(`Did all ${people.length} name tags print? Press OK to mark them as printed, so “Not printed yet” skips them next time.`)) {
        await api.post('/badges/printed', { ticketIds: people.map(p => p.ticket_id) });
        toast.success('Marked as printed.');
      }
    } finally { setPrinting(false); }
  };
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h2>Name Tags</h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ display: 'grid', gap: 14 }}>
          {b && <div style={{ textAlign: 'center' }}><div style={{ display: 'inline-block' }}><BadgeView design={b.design} person={(people && people[0]) || { first_name: 'Sample', last_name: 'Name', company: 'Company' }} event={b.event} scale={0.9} /></div></div>}
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Print tags for</label>
            <select className="form-select" value={who} onChange={e => setWho(e.target.value)}>
              <option value="all">Everyone with a ticket</option>
              <option value="unprinted">Only people whose tag hasn’t been printed yet</option>
              <option value="checked_in">Only people who have checked in</option>
            </select>
            <p className="form-hint">{people === null ? 'Counting…' : `${people.length} name tag${people.length === 1 ? '' : 's'}`}. Printing opens your device’s print window — choose the label printer (or the printer with the Avery sheets).</p>
          </div>
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <Link className="btn btn-ghost" to={`/events/${eventId}/badges`}>✏️ Change the design</Link>
          <span style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
            <button className="btn btn-primary" onClick={print} disabled={printing || !people || !people.length || !b}>{printing ? 'Printing…' : `🖨 Print ${people ? people.length : ''} tags`}</button>
          </span>
        </div>
      </div>
    </div>
  );
}

function SponsorDetailModal({ sponsor: s, onClose }) {
  const money = (v) => `$${parseFloat(v || 0).toFixed(2)}`;
  const contactName = [s.contact_first_name, s.contact_last_name].filter(Boolean).join(' ');
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2>{s.sponsor_name || s.business_name || 'Sponsor'}</h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
          {/* What this sponsorship is for — [marker: item-list v1] */}
          <div style={{ fontSize: '0.85rem', background: 'var(--gray-50)', borderRadius: 6, padding: '8px 10px' }}>
            <div><span style={{ color: 'var(--gray-500)' }}>For: </span>
              <strong>{s.tier_name || 'Sponsorship (no tier set)'}</strong>
              {s.event_name && <> — {s.event_name}{s.event_date ? `, ${new Date(s.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}</>}
            </div>
            {s.item_name && <div><span style={{ color: 'var(--gray-500)' }}>Item: </span><Link to="/products" onClick={onClose}>{s.item_name}</Link></div>}
            <div><span style={{ color: 'var(--gray-500)' }}>Invoice: </span>
              {s.invoice_id
                ? <Link to={`/invoices/${s.invoice_id}`} onClick={onClose}>{s.invoice_number}</Link>
                : 'none yet'}
              {s.invoice_id && <> · {s.invoice_status?.replace('_', ' ')}{s.invoice_balance != null && parseFloat(s.invoice_balance) > 0 ? ` · ${money(s.invoice_balance)} due` : ''}</>}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Tier</div>
              <div style={{ fontWeight: 600 }}>{s.tier_name || '—'}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Amount</div>
              <div style={{ fontWeight: 600 }}>{money(s.amount)}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Status</div>
              <span className={`badge badge-${s.payment_status}`}>{s.payment_status}</span>
            </div>
          </div>

          {(s.tier_price != null || s.tier_included_tickets != null) && (
            <div style={{ display: 'flex', gap: 24, fontSize: '0.875rem' }}>
              {s.tier_price != null && <div><span style={{ color: 'var(--gray-500)' }}>Tier price: </span>{money(s.tier_price)}</div>}
              {s.tier_included_tickets != null && <div><span style={{ color: 'var(--gray-500)' }}>Included tickets: </span>{s.tier_included_tickets}</div>}
            </div>
          )}

          {s.tier_benefits && (
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: 4 }}>Benefits</div>
              <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>{s.tier_benefits}</div>
            </div>
          )}

          <div style={{ display: 'grid', gap: 4, fontSize: '0.875rem', borderTop: '1px solid var(--gray-100)', paddingTop: 10 }}>
            {s.business_name && <div><span style={{ color: 'var(--gray-500)' }}>Business: </span>{s.business_name}</div>}
            {contactName && <div><span style={{ color: 'var(--gray-500)' }}>Contact: </span>{contactName}</div>}
            {s.sponsor_email && <div><span style={{ color: 'var(--gray-500)' }}>Email: </span>{s.sponsor_email}</div>}
            {s.paid_at && <div><span style={{ color: 'var(--gray-500)' }}>Paid: </span>{new Date(s.paid_at).toLocaleDateString()}</div>}
            {s.notes && <div><span style={{ color: 'var(--gray-500)' }}>Notes: </span>{s.notes}</div>}
          </div>
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function EditEventModal({ event, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:                  event.name || '',
    description:           event.description || '',
    internalNotes:         event.internal_notes || '',
    eventDate:             event.event_date ? new Date(event.event_date).toISOString().split('T')[0] : '',
    startTime:             event.start_time ? event.start_time.slice(0, 5) : '',
    endTime:               event.end_time ? event.end_time.slice(0, 5) : '',
    venue:                 event.venue || '',
    locationAddress:       event.location_address || '',
    totalCapacity:         event.total_capacity != null ? String(event.total_capacity) : '',
    registrationOpenDate:  event.registration_open_date ? new Date(event.registration_open_date).toISOString().split('T')[0] : '',
    registrationCloseDate: event.registration_close_date ? new Date(event.registration_close_date).toISOString().split('T')[0] : '',
    visibility:            event.visibility || 'public',
    status:                event.status || 'draft',
    attendeeTagId:         event.attendee_tag_id != null ? String(event.attendee_tag_id) : '',
  });
  const [saving, setSaving] = useState(false);
  const [tags, setTags] = useState([]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get('/contact-tags')
      .then(r => setTags((r.data || []).filter(t => t.is_active !== false)))
      .catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Event name is required.');
    if (!form.eventDate) return toast.error('Event date is required.');
    setSaving(true);
    try {
      const payload = {
        ...form,
        totalCapacity:         form.totalCapacity ? parseInt(form.totalCapacity) : null,
        startTime:             form.startTime || null,
        endTime:               form.endTime || null,
        registrationOpenDate:  form.registrationOpenDate || null,
        registrationCloseDate: form.registrationCloseDate || null,
        attendeeTagId:         form.attendeeTagId || null,
      };
      await api.patch(`/events/${event.id}`, payload);
      toast.success('Event updated.'); onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update event.');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Edit Event</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Name *</label>
            <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-input" rows={2} value={form.description} onChange={e => set('description', e.target.value)} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Event Date *</label>
              <input className="form-input" type="date" value={form.eventDate} onChange={e => set('eventDate', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Start Time</label>
              <input className="form-input" type="time" value={form.startTime} onChange={e => set('startTime', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">End Time</label>
              <input className="form-input" type="time" value={form.endTime} onChange={e => set('endTime', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Venue</label>
              <input className="form-input" value={form.venue} onChange={e => set('venue', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Total Capacity</label>
              <input className="form-input" type="number" min="0" value={form.totalCapacity} onChange={e => set('totalCapacity', e.target.value)} placeholder="Leave blank for unlimited" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Location Address</label>
            <input className="form-input" value={form.locationAddress} onChange={e => set('locationAddress', e.target.value)} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Registration Opens</label>
              <input className="form-input" type="date" value={form.registrationOpenDate} onChange={e => set('registrationOpenDate', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Registration Closes</label>
              <input className="form-input" type="date" value={form.registrationCloseDate} onChange={e => set('registrationCloseDate', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Visibility</label>
              <select className="form-select" value={form.visibility} onChange={e => set('visibility', e.target.value)}>
                <option value="public">Public</option>
                <option value="members_only">Members Only</option>
                <option value="private">Private</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="cancelled">Cancelled</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Attendee Tag</label>
            <select className="form-select" value={form.attendeeTagId} onChange={e => set('attendeeTagId', e.target.value)}>
              <option value="">— None —</option>
              {tags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: 4 }}>
              Everyone who buys or registers for this event is automatically given this tag. Existing attendees aren't changed retroactively.
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Internal Notes</label>
            <textarea className="form-input" rows={2} value={form.internalNotes} onChange={e => set('internalNotes', e.target.value)} placeholder="Not visible to attendees" />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 10, fontSize: '0.875rem' }}>
      <span style={{ color: 'var(--gray-500)', width: 120, flexShrink: 0, fontWeight: 500 }}>{label}</span>
      <span style={{ color: 'var(--gray-900)' }}>{value}</span>
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function FlyerPreview({ eventId, filename, originalName }) {
  const url = `/api/events/${eventId}/flyer`;
  const isPdf = filename && filename.toLowerCase().endsWith('.pdf');
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
      {isPdf ? (
        <div style={{
          width: 120, height: 160, background: '#fafafa', border: '1px solid #e5e5e5',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', borderRadius: 6, color: 'var(--gray-400)',
        }}>
          <div style={{ fontSize: 32 }}>📄</div><small>PDF</small>
        </div>
      ) : (
        <img src={url} alt="Event flyer"
             style={{ maxWidth: 240, maxHeight: 320, objectFit: 'contain', borderRadius: 6, border: '1px solid #e5e5e5' }} />
      )}
      <div>
        <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{originalName || filename}</div>
        <a href={url} target="_blank" rel="noreferrer"
           className="btn btn-secondary btn-sm" style={{ marginTop: 8, display: 'inline-block' }}>
          Open in new tab
        </a>
      </div>
    </div>
  );
}

function TicketTypeModal({ eventId, ticketType, productOptions, onClose, onSaved }) {
  const isEdit = !!ticketType;
  const [form, setForm] = useState({
    name:              ticketType?.name              ?? '',
    memberPrice:       ticketType?.member_price      ?? '',
    nonMemberPrice:    ticketType?.non_member_price  ?? '',
    quantityAvailable: ticketType?.quantity_available ?? '',
    catalogItemId:     ticketType?.catalog_item_id   ?? '',
    isActive:          ticketType?.is_active         ?? true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleProductPicked = (idStr) => {
    const id = idStr ? parseInt(idStr, 10) : null;
    setForm(f => {
      const next = { ...f, catalogItemId: id };
      if (id) {
        const product = productOptions.find(p => p.id === id);
        if (product) {
          if (!f.name || f.name.trim() === '') next.name = product.name;
          if (f.memberPrice === '' || f.memberPrice === null) next.memberPrice = String(product.default_price ?? '');
          if (f.nonMemberPrice === '' || f.nonMemberPrice === null) next.nonMemberPrice = String(product.default_price ?? '');
        }
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Ticket type name is required.'); return; }
    setSaving(true);
    try {
      const payload = {
        name:              form.name.trim(),
        memberPrice:       form.memberPrice    === '' ? 0    : parseFloat(form.memberPrice),
        nonMemberPrice:    form.nonMemberPrice === '' ? 0    : parseFloat(form.nonMemberPrice),
        quantityAvailable: form.quantityAvailable === '' ? null : parseInt(form.quantityAvailable, 10),
        catalogItemId:     form.catalogItemId || null,
      };
      if (isEdit) payload.isActive = !!form.isActive;
      if (isEdit) await api.patch(`/ticket-types/${ticketType.id}`, payload);
      else await api.post(`/events/${eventId}/ticket-types`, payload);
      toast.success(isEdit ? 'Ticket type updated.' : 'Ticket type created.');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save.');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Ticket Type' : 'New Ticket Type'}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label className="form-label">Product Template <small style={{ fontWeight: 400, color: '#888' }}>(optional)</small></label>
            <select className="form-select" value={form.catalogItemId || ''} onChange={e => handleProductPicked(e.target.value)}>
              <option value="">— None —</option>
              {productOptions.map(p => (
                <option key={p.id} value={p.id}>{p.name}{p.sku ? ` (${p.sku})` : ''}</option>
              ))}
            </select>
            <small style={{ color: '#888' }}>Picking a product fills in the name and price below.</small>
          </div>
          <div>
            <label className="form-label">Name *</label>
            <input className="form-input" value={form.name}
                   onChange={e => set('name', e.target.value)} placeholder="e.g. VIP Dinner" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Member Price</label>
              <input className="form-input" type="number" step="0.01" min="0"
                     value={form.memberPrice} onChange={e => set('memberPrice', e.target.value)} placeholder="0.00" />
            </div>
            <div>
              <label className="form-label">Non-Member Price</label>
              <input className="form-input" type="number" step="0.01" min="0"
                     value={form.nonMemberPrice} onChange={e => set('nonMemberPrice', e.target.value)} placeholder="0.00" />
            </div>
          </div>
          <div>
            <label className="form-label">
              Quantity Available <small style={{ fontWeight: 400, color: '#888' }}>(leave blank for unlimited)</small>
            </label>
            <input className="form-input" type="number" min="0" style={{ maxWidth: 180 }}
                   value={form.quantityAvailable} onChange={e => set('quantityAvailable', e.target.value)}
                   placeholder="Unlimited" />
            {isEdit && ticketType?.quantity_sold > 0 && (
              <small style={{ display: 'block', color: '#888', marginTop: 4 }}>
                {ticketType.quantity_sold} sold so far.
              </small>
            )}
          </div>
          {isEdit && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.isActive}
                     onChange={e => set('isActive', e.target.checked)} />
              <span><strong>Active</strong> — uncheck to stop selling this ticket type</span>
            </label>
          )}
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : (isEdit ? 'Save' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
}

function SponsorshipTierModal({ eventId, tier, onClose, onSaved }) {
  const isEdit = !!tier;
  const [form, setForm] = useState({
    name:                tier?.name                 ?? '',
    price:               tier?.price != null ? String(tier.price) : '',
    includedTickets:     tier?.included_tickets != null ? String(tier.included_tickets) : '',
    benefitsDescription: tier?.benefits_description ?? '',
    sortOrder:           tier?.sort_order != null ? String(tier.sort_order) : '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Tier name is required.'); return; }
    const price = parseFloat(form.price);
    if (Number.isNaN(price) || price <= 0) { toast.error('Enter a price greater than 0.'); return; }
    setSaving(true);
    try {
      const payload = {
        name:                form.name.trim(),
        price,
        includedTickets:     form.includedTickets === '' ? 0 : parseInt(form.includedTickets, 10),
        benefitsDescription: form.benefitsDescription.trim() || null,
        sortOrder:           form.sortOrder === '' ? 0 : parseInt(form.sortOrder, 10),
      };
      if (isEdit) await api.patch(`/sponsorship-tiers/${tier.id}`, payload);
      else await api.post(`/events/${eventId}/sponsorship-tiers`, payload);
      toast.success(isEdit ? 'Sponsorship tier updated.' : 'Sponsorship tier created.');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save tier.');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Sponsorship Tier' : 'New Sponsorship Tier'}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label className="form-label">Name *</label>
            <input className="form-input" value={form.name}
                   onChange={e => set('name', e.target.value)}
                   placeholder="e.g. Gold Sponsor" autoFocus />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Price * <small style={{ fontWeight: 400, color: '#888' }}>(USD)</small></label>
              <input className="form-input" type="number" step="0.01" min="0"
                     value={form.price} onChange={e => set('price', e.target.value)} placeholder="0.00" />
            </div>
            <div>
              <label className="form-label">Included Tickets <small style={{ fontWeight: 400, color: '#888' }}>(optional)</small></label>
              <input className="form-input" type="number" min="0"
                     value={form.includedTickets} onChange={e => set('includedTickets', e.target.value)}
                     placeholder="0" />
            </div>
          </div>
          <div>
            <label className="form-label">Benefits <small style={{ fontWeight: 400, color: '#888' }}>(optional)</small></label>
            <textarea className="form-input" rows={2}
                      value={form.benefitsDescription}
                      onChange={e => set('benefitsDescription', e.target.value)}
                      placeholder="e.g. Logo on banner, table for 8, full-page program ad" />
          </div>
          <div>
            <label className="form-label">Display Order <small style={{ fontWeight: 400, color: '#888' }}>(optional — lower shows first)</small></label>
            <input className="form-input" type="number" style={{ maxWidth: 180 }}
                   value={form.sortOrder} onChange={e => set('sortOrder', e.target.value)}
                   placeholder="0" />
          </div>
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : (isEdit ? 'Save' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Attendee grid columns ── [marker: grid-tickets-invoices v1]
function makeTicketColumns({ isManagerOrAbove, isSuperAdmin, canCheckIn = isManagerOrAbove, handleCheckIn, handleUndoCheckIn, setEditingTicket, setRefundTicket, handleDeleteTicket, setComposeTicketId }) {
  const human = (v) => (v ? String(v).replace(/_/g, ' ') : '');
  return [
    { key: 'ticket_number', label: 'Ticket #', width: 135, render: t => <span className="td-mono" style={{ fontSize: '0.75rem' }}>{t.ticket_number}</span> },
    { key: 'attendee', label: 'Attendee', width: 170, get: t => `${t.buyer_last_name || ''} ${t.buyer_first_name || ''}`.trim(),
      text: t => `${t.buyer_first_name || ''} ${t.buyer_last_name || ''}`.trim(),
      render: t => <span><strong>{t.buyer_first_name} {t.buyer_last_name}</strong>{t.needs_name && <span title="This ticket still needs the guest's name — use Guest names" style={{ marginLeft: 6, fontSize: '0.65rem', background: '#fff4d6', color: '#7a5200', border: '1px solid #e0b44c', borderRadius: 10, padding: '1px 6px' }}>name needed</span>}</span> }, /* [marker: guest-names v1] */
    { key: 'buyer_email', label: 'Email', width: 200,
      render: t => t.buyer_email ? <a href={`mailto:${t.buyer_email}`} onClick={e => e.stopPropagation()}>{t.buyer_email}</a> : '' },
    { key: 'organization', label: 'Organization', width: 170, get: t => t.buyer_business_name || t.business_name || '' },
    { key: 'ticket_type_name', label: 'Type', width: 120 },
    { key: 'amount_paid', label: 'Paid', width: 90, type: 'money' },
    { key: 'base_price', label: 'Price', width: 90, type: 'money', defaultHidden: true },
    { key: 'discount_amount', label: 'Discount', width: 90, type: 'money', defaultHidden: true },
    { key: 'promo_code', label: 'Promo', width: 90, defaultHidden: true },
    { key: 'payment_method', label: 'Payment', width: 100, text: t => human(t.payment_method) },
    { key: 'payment_status', label: 'Status', width: 95, text: t => human(t.payment_status),
      render: t => <span className={`badge badge-${t.payment_status}`} style={{ fontSize: '0.65rem' }}>{human(t.payment_status)}</span> },
    { key: 'member_price_applied', label: 'Member Price', width: 100, type: 'bool', defaultHidden: true },
    { key: 'order_ref', label: 'Order / Invoice', width: 140, defaultHidden: true },
    /* [marker: card-details v1] what Cardknox says about the card that paid — the card holder isn't always the guest */
    { key: 'card_name', label: 'Name on Card', width: 150 },
    { key: 'card_auth_code', label: 'Auth Code', width: 95, render: t => t.card_auth_code ? <span className="td-mono" style={{ fontSize: '0.75rem' }}>{t.card_auth_code}</span> : '' },
    { key: 'cardknox_transaction_id', label: 'Card Ref #', width: 115, render: t => t.cardknox_transaction_id ? <span className="td-mono" style={{ fontSize: '0.75rem' }}>{t.cardknox_transaction_id}</span> : '' },
    { key: 'card_last4', label: 'Card Last 4', width: 85, defaultHidden: true },
    { key: 'checked_in', label: 'Check-In', width: 150, type: 'bool',
      text: t => (t.checked_in ? 'Checked in' : 'Not checked in'),
      render: t => t.checked_in ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: '0.78rem', color: 'var(--success)' }}>✓ {t.checked_in_at ? new Date(t.checked_in_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''}</span>
          {canCheckIn() && (
            <button className="btn btn-ghost btn-sm" style={{ fontSize: '0.7rem', padding: '0 4px' }} onClick={e => { e.stopPropagation(); handleUndoCheckIn(t.id); }} title="Undo check-in">Undo</button>
          )}
        </span>
      ) : canCheckIn() ? (
        <button className="btn btn-secondary btn-sm" style={{ padding: '0 8px', fontSize: '0.72rem' }} onClick={e => { e.stopPropagation(); handleCheckIn(t); }}>Check In</button>
      ) : '' },
    { key: 'checked_in_at', label: 'Checked In At', width: 120, type: 'date', defaultHidden: true },
    { key: 'created_at', label: 'Added', width: 100, type: 'date', defaultHidden: true },
    { key: 'actions', label: '', width: 125, text: () => '',
      render: t => (
        <span style={{ display: 'inline-flex', gap: 2 }} onClick={e => e.stopPropagation()}>
          {isManagerOrAbove() && t.buyer_email && setComposeTicketId && (<button className="btn btn-ghost btn-sm" style={{ padding: '0 6px' }} onClick={() => setComposeTicketId(t.id)} title="Email ticket confirmation (review first)">✉</button>)}
          {isManagerOrAbove() && (<button className="btn btn-ghost btn-sm" style={{ padding: '0 6px' }} onClick={() => setEditingTicket(t)} title="Edit attendee">✎</button>)}
          {isSuperAdmin() && t.cardknox_transaction_id && t.payment_status !== 'refunded' && (<button className="btn btn-ghost btn-sm" style={{ padding: '0 6px', color: 'var(--danger)' }} onClick={() => setRefundTicket(t)} title="Refund this card payment">↩</button>)}
          {isManagerOrAbove() && (!t.cardknox_transaction_id || t.payment_status === 'refunded' || isSuperAdmin()) && (<button className="btn btn-ghost btn-sm" style={{ padding: '0 6px', color: 'var(--danger)' }} onClick={() => handleDeleteTicket(t)} title={t.cardknox_transaction_id && t.payment_status !== 'refunded' ? 'Refund & delete ticket' : 'Delete ticket'}>✕</button>)}
        </span>
      ) },
  ];
}

function AddTicketModal({ eventId, event, onClose, onSaved }) {
  const [mode, setMode] = useState('contact');
  const [form, setForm] = useState({
    ticketTypeId:      event.ticketTypes?.[0]?.id || '',
    contactId:         '',
    buyerFirstName:    '',
    buyerLastName:     '',
    buyerEmail:        '',
    buyerBusinessName: '',
    businessId:        '',
    paymentStatus:     'paid',
    paymentMethod:     'credit_card',
    promoCode:         '',
    isComp:            false,
    sendConfirmation:  false,
  });
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  // [marker: grid-tickets-invoices v1] several tickets in one go: one payer, an
  // attendee per ticket (ticket 1 = the buyer above; blanks become "Guest of …")
  const [qty, setQty] = useState(1);
  const [guests, setGuests] = useState([]); // for tickets 2..qty: { first, last, email }
  const setGuest = (i, k, v) => setGuests(g => { const n = [...g]; n[i] = { ...(n[i] || {}), [k]: v }; return n; });
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (mode !== 'contact' || !contactSearch || contactSearch.length < 2) {
      setContactResults([]); return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get(`/contacts?search=${encodeURIComponent(contactSearch)}&limit=10`);
        setContactResults(res.data.data || []);
      } catch (_) { setContactResults([]); }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [contactSearch, mode]);

  const pickContact = (c) => {
    setSelectedContact(c); setContactSearch(''); setContactResults([]);
    setForm(f => ({
      ...f, contactId: c.id,
      buyerFirstName: c.first_name, buyerLastName: c.last_name,
      buyerEmail: c.email || '', buyerBusinessName: c.business_name || '',
      businessId: c.business_id || '',
    }));
  };

  const clearContact = () => {
    setSelectedContact(null);
    setForm(f => ({
      ...f, contactId: '', businessId: '',
      buyerFirstName: '', buyerLastName: '',
      buyerEmail: '', buyerBusinessName: '',
    }));
  };

  const selectedType = event.ticketTypes?.find(tt => String(tt.id) === String(form.ticketTypeId));

  const handleSave = async () => {
    if (!form.ticketTypeId) return toast.error('Pick a ticket type.');
    if (!form.buyerFirstName?.trim() || !form.buyerLastName?.trim() || !form.buyerEmail?.trim()) {
      return toast.error('Buyer first name, last name, and email are required.');
    }
    const n = Math.max(1, Math.min(50, parseInt(qty, 10) || 1));
    if (selectedType && selectedType.quantity_available &&
        (selectedType.quantity_sold || 0) + n > selectedType.quantity_available) {
      return toast.error(`Only ${selectedType.quantity_available - (selectedType.quantity_sold || 0)} ticket(s) of this type are left.`);
    }
    if (n > 1) {
      setSaving(true);
      const orderRef = 'GRP-' + Date.now().toString(36).toUpperCase();
      const buyerName = `${form.buyerFirstName.trim()} ${form.buyerLastName.trim()}`;
      const base = {
        ticketTypeId:      parseInt(form.ticketTypeId, 10),
        buyerBusinessName: form.buyerBusinessName || null,
        businessId:        form.businessId ? parseInt(form.businessId, 10) : null,
        promoCode:         form.promoCode || null,
        paymentStatus:     form.isComp ? 'comp' : form.paymentStatus,
        isComp:            form.isComp,
        paymentMethod:     form.isComp ? null : form.paymentMethod,
        orderRef,
      };
      let made = 0;
      let firstTicketId = null;
      try {
        for (let i = 0; i < n; i++) {
          let p;
          if (i === 0) {
            p = { ...base, buyerFirstName: form.buyerFirstName.trim(), buyerLastName: form.buyerLastName.trim(),
                  buyerEmail: form.buyerEmail.trim(), contactId: form.contactId ? parseInt(form.contactId, 10) : null,
                  sendConfirmation: false };            // reviewed in the email window instead [marker: email-compose v1]
          } else {
            const g = guests[i - 1] || {};
            const ownEmail = (g.email || '').trim();
            p = { ...base,
                  buyerFirstName: (g.first || '').trim() || 'Guest of',
                  buyerLastName:  (g.last || '').trim() || buyerName,
                  buyerEmail:     ownEmail || form.buyerEmail.trim(),
                  contactId: null,
                  noAutoContact: !ownEmail,             // don't attach a guest to the payer's contact
                  sendConfirmation: false };
          }
          let created;
          try { created = await postTicket(eventId, p); }
          catch (e) { if (e instanceof SkippedDuplicate) continue; throw e; }
          if (i === 0) firstTicketId = created.data?.id || null;
          made++;
        }
        toast.success(`${made} tickets created for ${buyerName}.`);
        onSaved(form.sendConfirmation ? firstTicketId : null);
      } catch (err) {
        toast.error(`Created ${made} of ${n}: ${err.response?.data?.error || 'failed'}`);
        if (made > 0) onSaved();
      } finally { setSaving(false); }
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ticketTypeId:      parseInt(form.ticketTypeId, 10),
        buyerFirstName:    form.buyerFirstName.trim(),
        buyerLastName:     form.buyerLastName.trim(),
        buyerEmail:        form.buyerEmail.trim(),
        buyerBusinessName: form.buyerBusinessName || null,
        contactId:         form.contactId ? parseInt(form.contactId, 10) : null,
        businessId:        form.businessId ? parseInt(form.businessId, 10) : null,
        promoCode:         form.promoCode || null,
        paymentStatus:     form.isComp ? 'comp' : form.paymentStatus,
        isComp:            form.isComp,
        sendConfirmation:  false,   // reviewed in the email window instead [marker: email-compose v1]
        paymentMethod:     form.isComp ? null : form.paymentMethod,
      };
      const created = await postTicket(eventId, payload);
      toast.success(`Ticket created${form.contactId ? ' and contact tagged' : ''}.`);
      onSaved(form.sendConfirmation ? (created.data?.id || null) : null);
    } catch (err) {
      if (err instanceof SkippedDuplicate) return;
      toast.error(err.response?.data?.error || 'Failed to create ticket.');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <h2>Add Ticket</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 16, padding: 12, background: 'var(--gray-50)', borderRadius: 6 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="radio" checked={mode === 'contact'}
                     onChange={() => { setMode('contact'); clearContact(); }} />
              Existing contact
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="radio" checked={mode === 'manual'}
                     onChange={() => { setMode('manual'); clearContact(); }} />
              New / one-off buyer
            </label>
          </div>
          {mode === 'contact' && (
            <div>
              <label className="form-label">Find Contact</label>
              {selectedContact ? (
                <div style={{
                  padding: '10px 12px', background: '#eef6ff', borderRadius: 6,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{selectedContact.first_name} {selectedContact.last_name}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--gray-600)' }}>
                      {selectedContact.email}{selectedContact.business_name ? ` · ${selectedContact.business_name}` : ''}
                    </div>
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={clearContact}>Change</button>
                </div>
              ) : (
                <>
                  <input className="form-input" value={contactSearch}
                         onChange={e => setContactSearch(e.target.value)}
                         placeholder="Type name or email…" />
                  {searching && <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginTop: 4 }}>Searching…</div>}
                  {contactResults.length > 0 && (
                    <div style={{ marginTop: 4, border: '1px solid var(--gray-200)', borderRadius: 6, maxHeight: 200, overflowY: 'auto' }}>
                      {contactResults.map(c => (
                        <div key={c.id} onClick={() => pickContact(c)}
                             style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--gray-100)' }}
                             onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-50)'}
                             onMouseLeave={e => e.currentTarget.style.background = ''}>
                          <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{c.first_name} {c.last_name}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                            {c.email || 'no email'}{c.business_name ? ` · ${c.business_name}` : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          {(mode === 'manual' || selectedContact) && (
            <fieldset style={{ border: '1px solid var(--gray-200)', borderRadius: 6, padding: 12 }}>
              <legend style={{ padding: '0 6px', fontSize: '0.85rem', color: 'var(--gray-500)' }}>
                {selectedContact ? 'Buyer info (auto-filled, override if needed)' : 'Buyer info'}
              </legend>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label className="form-label">First Name *</label>
                  <input className="form-input" value={form.buyerFirstName}
                         onChange={e => set('buyerFirstName', e.target.value)} />
                </div>
                <div>
                  <label className="form-label">Last Name *</label>
                  <input className="form-input" value={form.buyerLastName}
                         onChange={e => set('buyerLastName', e.target.value)} />
                </div>
              </div>
              <div style={{ marginTop: 8 }}>
                <label className="form-label">Email *</label>
                <input className="form-input" type="email" value={form.buyerEmail}
                       onChange={e => set('buyerEmail', e.target.value)} />
              </div>
              <div style={{ marginTop: 8 }}>
                <label className="form-label">Business / Organization</label>
                <input className="form-input" value={form.buyerBusinessName}
                       onChange={e => set('buyerBusinessName', e.target.value)} />
              </div>
            </fieldset>
          )}
          <div>
            <label className="form-label">Ticket Type *</label>
            <select className="form-select" value={form.ticketTypeId}
                    onChange={e => set('ticketTypeId', e.target.value)}>
              {event.ticketTypes?.map(tt => (
                <option key={tt.id} value={tt.id}>
                  {tt.name} — Member ${tt.member_price} / Non-member ${tt.non_member_price}
                </option>
              ))}
            </select>
            {selectedType && (
              <small style={{ color: 'var(--gray-500)' }}>
                {selectedType.quantity_available
                  ? `${selectedType.quantity_sold || 0} sold of ${selectedType.quantity_available}`
                  : 'Unlimited capacity'}
              </small>
            )}
          </div>
          <div>
            <label className="form-label">Number of tickets</label>
            <input className="form-input" type="number" min="1" max="50" value={qty} style={{ maxWidth: 120 }}
                   onChange={e => setQty(e.target.value)} />
            {parseInt(qty, 10) > 1 && (
              <div style={{ marginTop: 8, border: '1px solid var(--gray-200)', borderRadius: 6, padding: 10 }}>
                <div style={{ fontSize: '0.8rem', color: 'var(--gray-600)', marginBottom: 6 }}>
                  One payer (the buyer above). Ticket 1 is the buyer; name the other attendees below.
                  Leave a row blank for “Guest of {form.buyerFirstName || 'buyer'} {form.buyerLastName}”.
                </div>
                {Array.from({ length: Math.min(49, (parseInt(qty, 10) || 1) - 1) }, (_, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '28px 1fr 1fr 1.4fr', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>#{i + 2}</span>
                    <input className="form-input" placeholder="First name" value={guests[i]?.first || ''} onChange={e => setGuest(i, 'first', e.target.value)} />
                    <input className="form-input" placeholder="Last name" value={guests[i]?.last || ''} onChange={e => setGuest(i, 'last', e.target.value)} />
                    <input className="form-input" type="email" placeholder="Email (optional)" value={guests[i]?.email || ''} onChange={e => setGuest(i, 'email', e.target.value)} />
                  </div>
                ))}
              </div>
            )}
          </div>
          <fieldset style={{ border: '1px solid var(--gray-200)', borderRadius: 6, padding: 12 }}>
            <legend style={{ padding: '0 6px', fontSize: '0.85rem', color: 'var(--gray-500)' }}>Payment</legend>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="radio" checked={!form.isComp && form.paymentStatus === 'paid'}
                       onChange={() => set('paymentStatus', 'paid')} disabled={form.isComp} />
                Paid
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="radio" checked={!form.isComp && form.paymentStatus === 'pending'}
                       onChange={() => set('paymentStatus', 'pending')} disabled={form.isComp} />
                Pending
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.isComp}
                       onChange={e => set('isComp', e.target.checked)} />
                Comp (free)
              </label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label className="form-label" style={{ margin: 0, fontSize: '0.85rem', color: 'var(--gray-500)' }}>Method</label>
              <select className="form-select" style={{ maxWidth: 200 }}
                      value={form.paymentMethod}
                      onChange={e => set('paymentMethod', e.target.value)}
                      disabled={form.isComp}>
                <option value="credit_card">Credit Card</option>
                <option value="cash">Cash</option>
                <option value="check">Check</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="form-label">Promo Code (optional)</label>
              <input className="form-input" value={form.promoCode}
                     onChange={e => set('promoCode', e.target.value)}
                     placeholder="e.g. SAVE10"
                     style={{ textTransform: 'uppercase', maxWidth: 240 }}
                     disabled={form.isComp} />
            </div>
            <div style={{ marginTop: 4, paddingTop: 10, borderTop: '1px solid var(--gray-100)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.sendConfirmation}
                       onChange={e => set('sendConfirmation', e.target.checked)} />
                <span>Email a confirmation — you'll review and edit it before it's sent
                  <span style={{ color: 'var(--gray-500)', fontSize: '0.8rem' }}> (goes to the buyer)</span>
                </span>
              </label>
            </div>
          </fieldset>
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-ghost"   onClick={onClose}    disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : (parseInt(qty, 10) > 1 ? `Create ${Math.min(50, parseInt(qty, 10))} Tickets` : 'Create Ticket')}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddSponsorshipModal({ eventId, event, sponsorship, onClose, onSaved }) {
  const isEdit = !!sponsorship;
  const tiers = event.sponsorshipTiers || [];
  const [form, setForm] = useState({
    sponsorshipTierId: sponsorship?.sponsorship_tier_id ?? '',
    sponsorName:       sponsorship?.sponsor_name       ?? '',
    sponsorEmail:      sponsorship?.sponsor_email      ?? '',
    amount:            sponsorship?.amount != null ? String(sponsorship.amount) : '',
    notes:             sponsorship?.notes              ?? '',
    contactId:         sponsorship?.contact_id         ?? '',
    businessId:        sponsorship?.business_id        ?? '',
    paymentStatus:     sponsorship?.payment_status     ?? 'paid',
  });
  const [mode, setMode] = useState(sponsorship?.contact_id ? 'contact' : 'manual');
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleTierPicked = (idStr) => {
    const id = idStr ? parseInt(idStr, 10) : '';
    setForm(f => {
      const next = { ...f, sponsorshipTierId: id };
      if (id) {
        const tier = tiers.find(t => t.id === id);
        if (tier && (f.amount === '' || f.amount === null)) {
          next.amount = String(tier.price ?? tier.amount ?? '');
        }
      }
      return next;
    });
  };

  useEffect(() => {
    if (mode !== 'contact' || !contactSearch || contactSearch.length < 2) {
      setContactResults([]); return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get(`/contacts?search=${encodeURIComponent(contactSearch)}&limit=10`);
        setContactResults(res.data.data || []);
      } catch (_) { setContactResults([]); }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [contactSearch, mode]);

  const pickContact = (c) => {
    setSelectedContact(c); setContactSearch(''); setContactResults([]);
    setForm(f => ({
      ...f,
      contactId:    c.id,
      businessId:   c.business_id || '',
      sponsorName:  f.sponsorName || `${c.first_name} ${c.last_name}${c.business_name ? ` (${c.business_name})` : ''}`,
      sponsorEmail: f.sponsorEmail || c.email || '',
    }));
  };

  const clearContact = () => {
    setSelectedContact(null);
    setForm(f => ({ ...f, contactId: '', businessId: '' }));
  };

  const handleSave = async () => {
    if (!form.sponsorName.trim()) return toast.error('Sponsor name is required.');
    const amt = parseFloat(form.amount);
    if (Number.isNaN(amt) || amt <= 0) return toast.error('Enter a valid amount.');
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/sponsorships/${sponsorship.id}`, {
          sponsorName:   form.sponsorName.trim(),
          sponsorEmail:  form.sponsorEmail.trim() || null,
          amount:        amt,
          paymentStatus: form.paymentStatus,
          paidAt:        form.paymentStatus === 'paid'
                          ? (sponsorship.paid_at || new Date().toISOString())
                          : null,
          notes:         form.notes.trim() || null,
        });
        toast.success('Sponsorship updated.');
      } else {
        const createPayload = {
          sponsorshipTierId: form.sponsorshipTierId || null,
          sponsorName:       form.sponsorName.trim(),
          sponsorEmail:      form.sponsorEmail.trim() || null,
          amount:            amt,
          notes:             form.notes.trim() || null,
          contactId:         form.contactId ? parseInt(form.contactId, 10) : null,
          businessId:        form.businessId ? parseInt(form.businessId, 10) : null,
        };
        const res = await api.post(`/events/${eventId}/sponsorships`, createPayload);
        const newId = res.data?.id;
        if (form.paymentStatus === 'paid' && newId) {
          await api.patch(`/sponsorships/${newId}`, {
            paymentStatus: 'paid',
            paidAt:        new Date().toISOString(),
          });
        }
        toast.success(`Sponsorship recorded${form.contactId ? ' and contact tagged' : ''}.`);
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save sponsorship.');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Sponsorship' : 'Add Sponsorship'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {tiers.length > 0 && (
            <div>
              <label className="form-label">Sponsorship Tier <small style={{ fontWeight: 400, color: '#888' }}>(optional)</small></label>
              <select className="form-select" value={form.sponsorshipTierId || ''} onChange={e => handleTierPicked(e.target.value)}>
                <option value="">— Custom (enter amount below) —</option>
                {tiers.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name} — ${parseFloat(t.price ?? t.amount ?? 0).toFixed(2)}
                  </option>
                ))}
              </select>
              <small style={{ color: '#888' }}>Picking a tier auto-fills the amount below.</small>
            </div>
          )}

          {!isEdit && (
            <div style={{ padding: 12, background: 'var(--gray-50)', borderRadius: 6 }}>
              <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="radio" checked={mode === 'contact'}
                         onChange={() => { setMode('contact'); clearContact(); }} />
                  Link to existing contact
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="radio" checked={mode === 'manual'}
                         onChange={() => { setMode('manual'); clearContact(); }} />
                  No contact link
                </label>
              </div>
              {mode === 'contact' && (
                <>
                  {selectedContact ? (
                    <div style={{
                      padding: '10px 12px', background: '#eef6ff', borderRadius: 6,
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{selectedContact.first_name} {selectedContact.last_name}</div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--gray-600)' }}>
                          {selectedContact.email}{selectedContact.business_name ? ` · ${selectedContact.business_name}` : ''}
                        </div>
                      </div>
                      <button className="btn btn-ghost btn-sm" onClick={clearContact}>Change</button>
                    </div>
                  ) : (
                    <>
                      <input className="form-input" value={contactSearch}
                             onChange={e => setContactSearch(e.target.value)}
                             placeholder="Type name or email…" />
                      {searching && <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginTop: 4 }}>Searching…</div>}
                      {contactResults.length > 0 && (
                        <div style={{ marginTop: 4, border: '1px solid var(--gray-200)', borderRadius: 6, maxHeight: 200, overflowY: 'auto', background: '#fff' }}>
                          {contactResults.map(c => (
                            <div key={c.id} onClick={() => pickContact(c)}
                                 style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--gray-100)' }}
                                 onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-50)'}
                                 onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                              <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{c.first_name} {c.last_name}</div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                                {c.email || 'no email'}{c.business_name ? ` · ${c.business_name}` : ''}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <small style={{ color: '#888', display: 'block', marginTop: 4 }}>
                        Linking a contact applies the "Sponsor" tag automatically.
                      </small>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          <div>
            <label className="form-label">Sponsor Name *</label>
            <input className="form-input" value={form.sponsorName}
                   onChange={e => set('sponsorName', e.target.value)}
                   placeholder="e.g. The Berger Organization" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Amount * <small style={{ fontWeight: 400, color: '#888' }}>(USD)</small></label>
              <input className="form-input" type="number" step="0.01" min="0"
                     value={form.amount} onChange={e => set('amount', e.target.value)} placeholder="0.00" />
            </div>
            <div>
              <label className="form-label">Sponsor Email</label>
              <input className="form-input" type="email" value={form.sponsorEmail}
                     onChange={e => set('sponsorEmail', e.target.value)} placeholder="contact@sponsor.com" />
            </div>
          </div>

          <fieldset style={{ border: '1px solid var(--gray-200)', borderRadius: 6, padding: 12 }}>
            <legend style={{ padding: '0 6px', fontSize: '0.85rem', color: 'var(--gray-500)' }}>Payment Status</legend>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="radio" checked={form.paymentStatus === 'paid'}
                       onChange={() => set('paymentStatus', 'paid')} />
                Paid
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="radio" checked={form.paymentStatus === 'pending'}
                       onChange={() => set('paymentStatus', 'pending')} />
                Pending
              </label>
            </div>
            <small style={{ color: '#888', display: 'block', marginTop: 6 }}>
              "Paid" records this in the Transactions ledger immediately. Use "Pending" if waiting on payment.
            </small>
          </fieldset>

          <div>
            <label className="form-label">Notes</label>
            <textarea className="form-input" rows={2}
                      value={form.notes}
                      onChange={e => set('notes', e.target.value)}
                      placeholder="e.g. check #1234, paid by wire on 6/15" />
          </div>
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-ghost"   onClick={onClose}    disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Record Sponsorship')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Invoices for anything belonging to this event ─────────────────────────
// [marker: invoice-links v1] tickets, sponsorships and any item linked to the event,
// whether the invoice was made here, from a company, a contact or the Invoices page.
function EventInvoices({ eventId }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/invoice-items/by-event/${eventId}/invoices`).then(r => setRows(r.data || [])).catch(() => setRows([]));
  }, [eventId]);
  return (
    <DataGrid
      gridKey="event-invoices"
      rows={rows || []}
      loading={rows === null}
      persistFilters={false}
      onRowClick={r => navigate(`/invoices/${r.id}`)}
      totals={['total_amount', 'amount_paid', 'balance_due']}
      height="calc(100vh - 380px)"
      emptyText="No invoices for this event yet."
      columns={[
        { key: 'invoice_number', label: 'Invoice #', width: 120 },
        { key: 'issue_date', label: 'Date', width: 105, type: 'date', get: r => (r.issue_date ? r.issue_date + 'T00:00:00' : null) },
        { key: 'bill_to', label: 'Bill To', width: 220 },
        { key: 'items', label: 'For', width: 280 },
        { key: 'total_amount', label: 'Total', width: 110, type: 'money', align: 'right' },
        { key: 'amount_paid', label: 'Paid', width: 110, type: 'money', align: 'right' },
        { key: 'balance_due', label: 'Balance', width: 110, type: 'money', align: 'right' },
        { key: 'status', label: 'Status', width: 110, render: r => <span className={`badge badge-${r.status}`}>{String(r.status).replace('_', ' ')}</span> },
      ]}
    />
  );
}

// ─── Guest names for multi-ticket orders ───────────────────────────────────── [marker: guest-names v1]
function GuestNamesModal({ eventId, canEdit, onClose, onSaved }) {
  const [orders, setOrders] = useState(null);
  const [edits, setEdits] = useState({});      // ticketId -> { firstName, lastName, email }
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(null);
  const load = async () => {
    try { const r = await api.get(`/events/${eventId}/guest-names`); setOrders(r.data.orders || []); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not load guest names.'); setOrders([]); }
  };
  useEffect(() => { load(); }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps
  const val = (t, k) => {
    const e = edits[t.id];
    if (e && k in e) return e[k];
    if (k === 'email') return '';
    if (t.needs_name) return '';
    return k === 'firstName' ? t.buyer_first_name : t.buyer_last_name;
  };
  const setVal = (t, k, v) => setEdits(prev => ({ ...prev, [t.id]: { ...(prev[t.id] || {}), [k]: v } }));
  const changed = Object.entries(edits).filter(([, e]) => Object.values(e).some(x => String(x || '').trim()));
  const save = async () => {
    const rows = [];
    for (const [id, e] of changed) {
      const t = orders.flatMap(o => o.tickets).find(x => String(x.id) === String(id));
      const first = (e.firstName ?? val(t, 'firstName') ?? '').trim();
      const last = (e.lastName ?? val(t, 'lastName') ?? '').trim();
      if (!first || !last) return toast.error(`Ticket ${t.ticket_number}: enter both a first and last name.`);
      rows.push({ id: t.id, firstName: first, lastName: last, email: (e.email || '').trim() });
    }
    if (!rows.length) return toast.error('Type at least one guest’s name first.');
    setSaving(true);
    try {
      await api.post(`/events/${eventId}/guest-names`, { tickets: rows });
      toast.success(rows.length === 1 ? 'Guest name saved.' : `${rows.length} guest names saved.`);
      setEdits({}); onSaved(); await load();
    } catch (err) { toast.error(err.response?.data?.error || 'Could not save the names.'); }
    finally { setSaving(false); }
  };
  const ask = async (o) => {
    setSending(o.order_ref);
    try {
      const r = await api.post(`/events/${eventId}/guest-names/request`, { orderRef: o.order_ref });
      toast.success(`Email sent to ${r.data.sentTo}. Names they enter show up here automatically.`);
      await load();
    } catch (err) { toast.error(err.response?.data?.error || 'The email could not be sent.'); }
    finally { setSending(null); }
  };
  const when = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><span className="modal-title">Guest names</span><button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <p style={{ margin: '0 0 14px', fontSize: '0.85rem', color: 'var(--gray-600)' }}>
            These orders have tickets that still carry the buyer's name or "Guest of…". Type each guest's name, or email the buyer a link to fill them in.
            The names go on name tags, check-in and the attendee list.
          </p>
          {orders === null ? <p className="text-muted">Loading…</p> : orders.length === 0 ? (
            <div className="empty-state" style={{ padding: 24 }}><p>Every ticket has a guest's name. 🎉</p></div>
          ) : orders.map(o => (
            <div key={o.order_ref} style={{ border: '1px solid var(--gray-200)', borderRadius: 8, marginBottom: 14 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                <div style={{ fontSize: '0.85rem' }}>
                  <strong>{o.payer.first} {o.payer.last}</strong>{o.payer.business ? ` · ${o.payer.business}` : ''}
                  <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                    Order {o.order_ref} · {o.tickets.length} tickets{o.payer.email ? ` · ${o.payer.email}` : ''}
                    {o.answered_at ? ` · buyer replied ${when(o.answered_at)}` : o.requested_at ? ` · names requested ${when(o.requested_at)}` : ''}
                  </div>
                </div>
                {canEdit && (
                  <button type="button" className="btn btn-secondary btn-sm" disabled={sending === o.order_ref || !o.payer.email} onClick={() => ask(o)}>
                    {sending === o.order_ref ? 'Sending…' : o.requested_at ? 'Email the buyer again' : 'Email the buyer for names'}
                  </button>
                )}
              </div>
              <div style={{ padding: '6px 12px 10px' }}>
                {o.tickets.map((t, i) => (
                  <div key={t.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 0.8fr) 1fr 1fr 1.3fr', gap: 8, alignItems: 'center', padding: '5px 0' }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                      {t.ticket_number}<br />{i === 0 ? 'Buyer' : t.ticket_type_name}{t.checked_in ? ' · checked in' : ''}
                    </div>
                    {t.needs_name && canEdit ? (<>
                      <input className="form-input" placeholder="First name" aria-label={`First name for ${t.ticket_number}`} value={val(t, 'firstName')} onChange={e => setVal(t, 'firstName', e.target.value)} />
                      <input className="form-input" placeholder="Last name" aria-label={`Last name for ${t.ticket_number}`} value={val(t, 'lastName')} onChange={e => setVal(t, 'lastName', e.target.value)} />
                      <input className="form-input" type="email" placeholder="Email (optional)" aria-label={`Email for ${t.ticket_number}`} value={val(t, 'email')} onChange={e => setVal(t, 'email', e.target.value)} />
                    </>) : (
                      <div style={{ gridColumn: 'span 3', fontSize: '0.875rem' }}>
                        {t.buyer_first_name} {t.buyer_last_name}{t.needs_name ? <span style={{ color: '#7a5200' }}> — name needed</span> : ''}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Close</button>
          {canEdit && orders && orders.length > 0 && (
            <button className="btn btn-primary" onClick={save} disabled={saving || !changed.length}>{saving ? 'Saving…' : 'Save names'}</button>
          )}
        </div>
      </div>
    </div>
  );
}
